// accountStore — the authenticated identity + account-context spine (contract 07 §7). Sharpened
// from v3 `stores/accountStore.ts` (123 ln): already TS and store-shaped, so this is a sharpen,
// not a rebuild. Changes vs v3:
//   • DROP the dead feature-gate getters `workflowActive`/`orpheusActive`/`dashboardActive`
//     (analysis §2: zero live consumers) and the getters that LEAVE for their own features —
//     `isAH` → 19, `canAccessWorkspaceInsights` → 22 (contract §3).
//   • ADD the R10-assigned account-context getters `accountUserId`/`accountEmail`/`accountLevel`
//     (selected-account owner + caller level; live legacy importers — dropping them breaks an
//     export-job id and a settings-tab gate). `isCreativePkg` does NOT live here — it is a package
//     concern routed to `PackageService.isCreativePackage` (R10).
//   • `isSysAdmin` is SERVER-AUTHORITATIVE — it reads the bootstrap `rbac` section
//     (BE-ACC-01 / ORP-18806). The client-side `SYS_ADMIN_IDS` allowlist it used to read is
//     DELETED, not demoted to a fallback: audit ORP-18613 forbids a browser id-set deciding
//     authority at all (it shipped privileged ids in a public bundle and needed a frontend
//     deploy to grant/revoke admin).
//   • FIX the persistence hole (analysis R9/§C): v3 `persist` had no path list, so it persisted
//     EVERYTHING — including `permissions`, i.e. stale RBAC survived a refresh. v4 persists a
//     selection SUBSET only (`oldSelectedAccount` + `account` + `accounts` for a warm start);
//     `user`/`accountDetail`/`permissions`/`packageId`/`rbac` are re-fetched every boot.
// Setup-store form to match the feature's `sessionStore`/auth `authStore`; it satisfies the
// injected `AccountHydrationTarget` write-surface (`hydrateOwn.ts`) via its setters.
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type {
  AccountDetail,
  AccountItem,
  PermissionMap,
  PermissionValue,
  UserAccessInfo,
  UserResponse,
} from '../models/account.types'
import { CREATE_META_PAGE_AD_ACCOUNT_IDS } from '../models/account.types'
import type { CapabilityKey, RbacSection } from '../models/bootstrap.types'
import { accountPersistStorage } from '../storage/AccountStorage'

export const useAccountStore = defineStore(
  'accountStore',
  () => {
    // ── state (contract §7) ────────────────────────────────────────────
    const accounts = ref<AccountItem[]>([])
    const account = ref<AccountItem | null>(null)
    const user = ref<UserResponse | null>(null)
    const accountDetail = ref<AccountDetail | null>(null)
    const permissions = ref<PermissionMap>({})
    const userAccessInfo = ref<UserAccessInfo>({})
    const packageId = ref<number | null>(null)
    const oldSelectedAccount = ref<Record<string, number>>({})
    /** The authoritative `rbac` section, verbatim. `null` until the server says otherwise — that
     *  default IS the cold-boot safety: no payload ⇒ no grant (contract §12/§14). */
    const rbac = ref<RbacSection | null>(null)

    // ── getters (contract §7 + R10) ────────────────────────────────────
    /** The authenticated user's own id (identity), not the selected-account owner. */
    const userId = computed<number | undefined>(() => user.value?.user?.id)
    const userEmail = computed<string | undefined>(
      () => user.value?.user?.email ?? user.value?.email,
    )
    const accountId = computed<number | undefined>(() => account.value?.id)
    const canAccessCreateMetaPageAd = computed<boolean>(
      () => accountId.value !== undefined && CREATE_META_PAGE_AD_ACCOUNT_IDS.has(accountId.value),
    )

    /** R10: the selected account's OWNER id — powers an export-job endpoint id (v3 `:40`). */
    const accountUserId = computed<number | undefined>(() => accountDetail.value?.user?.id)
    /** R10: the selected account's owner email (v3 `:41`). */
    const accountEmail = computed<string | undefined>(() => accountDetail.value?.user?.email)
    /** R10: the caller's access level on the selected account — powers a settings-tab gate
     *  (v3 `selectedAccountLevel`, `:45-46`). */
    const accountLevel = computed<unknown>(
      () =>
        userAccessInfo.value?.accounts?.find((acc) =>
          acc.id?.includes?.(account.value?.id as number),
        )?.level,
    )

    /**
     * SERVER-AUTHORITATIVE sysadmin (`rbac.is_sysadmin`, BE-ACC-01). Never an id-set, never an
     * email literal, and with no client fallback by design: pre-`ready` (or when the section
     * failed) `rbac` is `null` → `false`, so a cold boot cannot false-grant admin.
     */
    const isSysAdmin = computed<boolean>(() => rbac.value?.is_sysadmin ?? false)
    /**
     * SERVER-AUTHORITATIVE admin-bypass persona (`rbac.can_access_admin_panel`, i.e. the backend's
     * `is_admin` flag OR the sysadmin identity — a SUPERSET of `isSysAdmin`). Same never-persisted,
     * no-client-fallback discipline as the bit above, and the same cold-boot default of `false`.
     *
     * `=== true`, not `?? false`: the field is optional on the wire and this bit GRANTS exemptions,
     * so an absent or non-boolean value must read as "not an admin" (see `RbacSection`).
     */
    const canAccessAdminPanel = computed<boolean>(() => rbac.value?.can_access_admin_panel === true)
    /** `account.user_id === caller.id`, server-computed (`rbac.is_account_holder`). */
    const isAccountHolder = computed<boolean>(() => rbac.value?.is_account_holder ?? false)
    /** Server-computed role labels on the selected account (`owner`/`admin`/…). */
    const roles = computed<string[]>(() => rbac.value?.roles ?? [])
    /**
     * Role-derived workspace write capabilities (BE-25-08). `{}` is UNANSWERED, not denied — the
     * backend only fills it for a bootstrap that carried a `workspace_id`, so `useRbac` reads each
     * entry three-state rather than defaulting it false. See `RbacSection.capabilities`.
     */
    const capabilities = computed<Partial<Record<CapabilityKey, boolean>>>(
      () => rbac.value?.capabilities ?? {},
    )

    // ── setters (contract §7; the injected AccountHydrationTarget surface) ──
    function setUser(next: UserResponse | null): void {
      user.value = next
    }
    function setAccounts(next: AccountItem[]): void {
      accounts.value = next
    }
    function setAccount(next: AccountItem | null): void {
      account.value = next
    }
    function setAccountDetail(next: AccountDetail | null): void {
      accountDetail.value = next
    }
    function setPermissions(next: PermissionMap): void {
      permissions.value = next
    }
    function setUserAccessInfo(next: UserAccessInfo): void {
      userAccessInfo.value = next
    }
    function setPackageId(next: number | null): void {
      packageId.value = next
    }
    function setRbac(next: RbacSection | null): void {
      rbac.value = next
    }
    /** Clear only workspace-scoped capabilities when the selected workspace changes. */
    function clearRbacCapabilities(): void {
      if (rbac.value === null) return
      rbac.value = { ...rbac.value, capabilities: {} }
    }
    /** Merge a new account selection into the per-user old-selection cache (pure merge). */
    function setOldSelectedAccount(userKey: string, id: number): void {
      oldSelectedAccount.value = { ...oldSelectedAccount.value, [userKey]: id }
    }

    /**
     * `getPermission(key)` → the raw permission value; `getPermission(key[])` → the array of the
     * truthy values (or `false` when none are). Behaviour is identical to legacy `accountStore`
     * (single / array / missing), so existing callers port 1:1.
     */
    function getPermission(perm: string | string[]): PermissionValue | PermissionValue[] | false {
      if (Array.isArray(perm)) {
        const found = perm
          .map((key) => permissions.value?.[key])
          .filter(Boolean) as PermissionValue[]
        return found.length < 1 ? false : found
      }
      return permissions.value?.[perm]
    }

    /** Logout clears live account/session context but keeps the non-authoritative per-user
     *  selection history. The next authenticated bootstrap validates that id against the fresh
     *  account list before using it, so stale or inaccessible ids fall through safely. */
    function resetState(): void {
      accounts.value = []
      account.value = null
      user.value = null
      accountDetail.value = null
      permissions.value = {}
      userAccessInfo.value = {}
      packageId.value = null
      rbac.value = null
    }

    return {
      accounts,
      account,
      user,
      accountDetail,
      permissions,
      userAccessInfo,
      packageId,
      oldSelectedAccount,
      rbac,
      userId,
      userEmail,
      accountId,
      canAccessCreateMetaPageAd,
      accountUserId,
      accountEmail,
      accountLevel,
      isSysAdmin,
      canAccessAdminPanel,
      isAccountHolder,
      roles,
      capabilities,
      setUser,
      setAccounts,
      setAccount,
      setAccountDetail,
      setPermissions,
      setUserAccessInfo,
      setPackageId,
      setRbac,
      clearRbacCapabilities,
      setOldSelectedAccount,
      getPermission,
      resetState,
    }
  },
  // Persist a SELECTION SUBSET only (contract §7 / analysis R9): the old-selection cache + the
  // current account + the account list (warm fast-path on refresh). `permissions`/`user`/
  // `accountDetail`/`packageId`/`rbac` are NEVER persisted — no stale RBAC survives a hard
  // refresh, so `isSysAdmin` must be re-granted by the server on every boot. Adding `rbac` to
  // this `pick` list would put a self-asserted admin bit in browser storage; do not.
  {
    persist: {
      key: 'accountStore',
      pick: ['oldSelectedAccount', 'account', 'accounts'],
      storage: accountPersistStorage,
    },
  },
)

export type AccountStore = ReturnType<typeof useAccountStore>
